import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, TreeDeciduous, Castle, BookOpen, MapPin, Camera, Heart } from 'lucide-react';
import './GuidePage.css';

export default function LowlandsGuide() {
  return (
    <div className="guide-page">
      <Link to="/wissen" className="back-link"><ArrowLeft size={20} /> Zurück zum Wissen</Link>
      
      <article className="guide-content">
        <h1>🌳 Lowlands & Borders - Das sanfte Schottland</h1>
        
        <div className="info-box">
          <p>
            Die Lowlands sind das Gegenteil der Highlands: <strong>Sanfte Hügel statt steile Berge, 
            grüne Täler statt karger Wildnis, historische Abteien statt einsamer Glens.</strong> 
            Perfekt für entspanntes Radfahren mit viel Kultur & Geschichte!
          </p>
        </div>

        <section>
          <h2>Übersicht Lowlands & Borders</h2>
          
          <div className="data-grid">
            <div className="data-item">
              <h3>📍 Geografie</h3>
              <p>Südlich der Highland Line</p>
              <p>Grenzgebiet zu England</p>
            </div>
            <div className="data-item">
              <h3>🌄 Landschaft</h3>
              <p>Sanfte Hügel (200-600m)</p>
              <p>Grüne Täler & Flüsse</p>
            </div>
            <div className="data-item">
              <h3>🏰 Charakter</h3>
              <p>Historisch & literarisch</p>
              <p>Abteien, Burgen, Walter Scott</p>
            </div>
            <div className="data-item">
              <h3>⭐ Schwierigkeit</h3>
              <p>Leicht bis Mittel</p>
              <p>Perfekt für Anfänger!</p>
            </div>
          </div>

          <h3>Warum Lowlands & Borders?</h3>
          <ul>
            <li><strong>Anfängerfreundlich:</strong> Keine extremen Anstiege wie Highlands</li>
            <li><strong>Gute Infrastruktur:</strong> Mehr Shops, Cafés, Unterkünfte als Highlands</li>
            <li><strong>Geschichte pur:</strong> Abteien, Burgen, Walter Scott's Heimat</li>
            <li><strong>Weniger Touristen:</strong> Nicht so überlaufen wie Highlands/Skye</li>
            <li><strong>Besseres Wetter:</strong> Weniger Regen als Westküste</li>
            <li><strong>Literarisch:</strong> Robert Burns, Walter Scott, Border Ballads</li>
          </ul>
        </section>

        <section>
          <h2>1. Scottish Borders - Das Grenzland</h2>
          
          <div className="info-box">
            <p>
              Die Scottish Borders waren jahrhundertelang umkämpftes Grenzgebiet zwischen 
              Schottland & England. Heute: Friedliche Region mit <strong>4 großen Abteien, 
              zahlreichen Burgen und der Heimat von Walter Scott</strong>.
            </p>
          </div>

          <h3>Borders Abbeys Way (100km, 3-4 Tage)</h3>
          <div className="info-box">
            <ul>
              <li><strong>Route:</strong> Rundtour zu den 4 großen Abteien</li>
              <li><strong>Start/Ziel:</strong> Flexible (Melrose empfohlen)</li>
              <li><strong>Distanz:</strong> 100km</li>
              <li><strong>Höhenmeter:</strong> ~1500m (moderat)</li>
              <li><strong>Schwierigkeit:</strong> Leicht-Mittel</li>
              <li><strong>Beste Zeit:</strong> April - Oktober</li>
            </ul>
          </div>

          <h4>Die 4 Border Abbeys</h4>
          <div className="checklist">
            <div className="check-item">
              <Castle /> <strong>Melrose Abbey:</strong> Schönste Ruine, Herz von Robert the Bruce begraben
            </div>
            <div className="check-item">
              <Castle /> <strong>Dryburgh Abbey:</strong> Ruhigste, Walter Scott begraben
            </div>
            <div className="check-item">
              <Castle /> <strong>Jedburgh Abbey:</strong> Besterhaltene, beeindruckende Architektur
            </div>
            <div className="check-item">
              <Castle /> <strong>Kelso Abbey:</strong> Größte (Ruine), am River Tweed
            </div>
          </div>

          <h3>Etappenvorschlag Borders Abbeys Way</h3>
          
          <h4>Tag 1: Melrose - Jedburgh (25km, 400m HM)</h4>
          <ul>
            <li><strong>Start:</strong> Melrose Abbey besichtigen</li>
            <li><strong>Route:</strong> A68 (vorsicht Verkehr!) oder Nebenstraßen via Harestanes</li>
            <li><strong>Highlights:</strong> Eildon Hills Aussicht, Waterloo Monument</li>
            <li><strong>Ziel:</strong> Jedburgh Abbey & Mary Queen of Scots House</li>
          </ul>

          <h4>Tag 2: Jedburgh - Kelso (20km, 300m HM)</h4>
          <ul>
            <li><strong>Route:</strong> B6400 via Cessford</li>
            <li><strong>Highlights:</strong> River Teviot, Roxburgh Castle (Ruine)</li>
            <li><strong>Ziel:</strong> Kelso Abbey & Floors Castle (schönstes bewohntes Schloss)</li>
          </ul>

          <h4>Tag 3: Kelso - Dryburgh (30km, 400m HM)</h4>
          <ul>
            <li><strong>Route:</strong> B6404 + B6356 via Smailholm</li>
            <li><strong>Highlights:</strong> Smailholm Tower, Scott's View (bester Ausblick Borders!)</li>
            <li><strong>Ziel:</strong> Dryburgh Abbey (Walter Scott's Grab)</li>
          </ul>

          <h4>Tag 4: Dryburgh - Melrose (25km, 400m HM)</h4>
          <ul>
            <li><strong>Route:</strong> B6404 via Newtown St Boswells</li>
            <li><strong>Highlights:</strong> Abbotsford House (Walter Scott's Heim)</li>
            <li><strong>Zurück:</strong> Melrose</li>
          </ul>

          <h3>Weitere Top-Routen Borders</h3>

          <h4>1. Tweed Cycle Route (150km, Peebles - Berwick)</h4>
          <ul>
            <li>Folgt dem River Tweed von Quelle bis Mündung</li>
            <li>Mix aus Straße & Off-Road (Gravel geeignet)</li>
            <li>Landschaftlich wunderschön, moderat hügelig</li>
            <li>3-4 Tage Dauer</li>
          </ul>

          <h4>2. Innerleithen - Traquair Loop (40km, 500m HM)</h4>
          <ul>
            <li><strong>Highlights:</strong> Traquair House (ältestes bewohntes Haus Schottlands)</li>
            <li>Durch Tweed Valley</li>
            <li>MTB-Mekka: Innerleithen hat Downhill-Trails (optional!)</li>
          </ul>

          <h4>3. Common Ridings (Sommer-Event Routen)</h4>
          <div className="pro-tip">
            <h3><Heart size={20} /> Common Ridings Tradition</h3>
            <p>
              Im Sommer feiern Borders-Städte "Common Ridings" - historische Reitfeste, 
              bei denen die Stadt-Grenzen abgeritten werden. <strong>Als Radfahrer kannst 
              du die Routen nachfahren!</strong>
            </p>
            <ul>
              <li><strong>Hawick:</strong> Juni (60km Route)</li>
              <li><strong>Selkirk:</strong> Juni (50km Route)</li>
              <li><strong>Galashiels:</strong> Juni (40km Route)</li>
              <li><strong>Jedburgh:</strong> Juli (Callant's Festival)</li>
            </ul>
          </div>

          <h3>Borders-Städte & Orte</h3>
          <div className="data-grid">
            <div className="data-item">
              <strong>Melrose:</strong> Beste Basis, schönste Abtei, touristisch
            </div>
            <div className="data-item">
              <strong>Peebles:</strong> Hübsches Städtchen, gute Shops
            </div>
            <div className="data-item">
              <strong>Galashiels:</strong> Größte Stadt, praktisch aber nicht schön
            </div>
            <div className="data-item">
              <strong>Kelso:</strong> Marktstadt, französischer Platz, Floors Castle
            </div>
          </div>
        </section>

        <section>
          <h2>2. Ayrshire & Burns Country</h2>
          
          <div className="info-box">
            <p>
              Ayrshire an der Westküste der Lowlands ist die Heimat von <strong>Robert Burns</strong>, 
              Schottlands Nationaldichter. Sanfte Küstenrouten, Burns-Heritage und Blick auf Arran!
            </p>
          </div>

          <h3>Burns Heritage Trail (80km, 2 Tage)</h3>
          <div className="info-box">
            <ul>
              <li><strong>Start:</strong> Ayr</li>
              <li><strong>Ziel:</strong> Dumfries (oder Rundtour)</li>
              <li><strong>Distanz:</strong> 80km one-way (160km return)</li>
              <li><strong>Schwierigkeit:</strong> Leicht (meist flach)</li>
              <li><strong>Highlights:</strong> Burns Cottage, Tam o' Shanter Experience, Burns Monument</li>
            </ul>
          </div>

          <h4>Burns-Stätten</h4>
          <div className="checklist">
            <div className="check-item">
              <BookOpen /> <strong>Burns Cottage (Alloway):</strong> Geburtshaus von Robert Burns
            </div>
            <div className="check-item">
              <BookOpen /> <strong>Brig o' Doon:</strong> Berühmte Brücke aus "Tam o' Shanter"
            </div>
            <div className="check-item">
              <BookOpen /> <strong>Burns Monument:</strong> Neoklassisches Denkmal
            </div>
            <div className="check-item">
              <BookOpen /> <strong>Burns House Museum (Dumfries):</strong> Wo Burns starb
            </div>
          </div>

          <h3>Ayrshire Coastal Route (120km, 3 Tage)</h3>
          <ul>
            <li><strong>Route:</strong> Ayr - Troon - Irvine - Largs - Gourock</li>
            <li><strong>Highlights:</strong> Culzean Castle, Isle of Arran Aussicht, Küsten-Dörfer</li>
            <li><strong>Charakter:</strong> Flach, windanfällig, familienfreundlich</li>
            <li><strong>Fähre:</strong> Von Ardrossan nach Arran möglich (Insel-Abstecher!)</li>
          </ul>

          <h3>Culzean Castle & Country Park</h3>
          <div className="pro-tip">
            <h3><Castle size={20} /> Must-See: Culzean Castle</h3>
            <p>
              Eines der schönsten Schlösser Schottlands, an dramatischen Klippen gelegen. 
              <strong>600 Hektar Country Park mit Radwegen!</strong>
            </p>
            <ul>
              <li>Von Ayr: 25km Richtung Süden (A719)</li>
              <li>Eintritt Park: £12 (Rad gratis mitnehmen)</li>
              <li>Interne Radwege: 15km einfach zu fahren</li>
            </ul>
          </div>
        </section>

        <section>
          <h2>3. Dumfries & Galloway - Der tiefe Südwesten</h2>
          
          <h3>Die vergessene Ecke</h3>
          <div className="info-box">
            <p>
              Dumfries & Galloway ist Schottlands am wenigsten besuchte Region - und genau 
              deshalb ein <strong>Geheimtipp für Radfahrer!</strong> Einsam, grün, hügelig 
              aber nicht extrem.
            </p>
          </div>

          <h4>Castle Douglas - Kirkcudbright Loop (60km, 600m HM)</h4>
          <ul>
            <li><strong>Route:</strong> A711 Küstenstraße (Solway Coast)</li>
            <li><strong>Highlights:</strong> Threave Castle, Kirkcudbright (Künstlerstadt)</li>
            <li><strong>Charakter:</strong> Ruhig, malerisch, wenig Verkehr</li>
          </ul>

          <h4>Galloway Forest Park</h4>
          <div className="info-box">
            <ul>
              <li><strong>Dark Sky Park:</strong> Erster in UK! Perfekt für Nachthimmel-Fotos</li>
              <li><strong>7Stanes MTB Trails:</strong> Weltklasse Mountain Biking (Glentrool, Kirroughtree)</li>
              <li><strong>Rennrad:</strong> A712 "Queen's Way" (spektakuläre Waldstraße)</li>
            </ul>
          </div>

          <h4>Mull of Galloway</h4>
          <ul>
            <li><strong>Südlichster Punkt Schottlands!</strong> (wie John o' Groats im Norden)</li>
            <li><strong>Von Stranraer:</strong> 40km, 500m HM</li>
            <li><strong>Leuchtturm:</strong> Kann besichtigt werden</li>
            <li><strong>Aussicht:</strong> Bei gutem Wetter: Isle of Man, Nordirland, England sichtbar</li>
          </ul>
        </section>

        <section>
          <h2>4. Stirlingshire & Clackmannanshire</h2>
          
          <h3>Die historische Mitte</h3>
          <div className="info-box">
            <p>
              Stirling = <strong>"Gateway to the Highlands"</strong> und Schauplatz wichtiger 
              Schlachten (Stirling Bridge 1297, Bannockburn 1314). Perfekte Mischung aus Geschichte 
              und Natur!
            </p>
          </div>

          <h4>Stirling - Doune - Callander Loop (70km, 700m HM)</h4>
          <ul>
            <li><strong>Start:</strong> Stirling (Castle besichtigen!)</li>
            <li><strong>Route:</strong> A84 via Doune</li>
            <li><strong>Highlights:</strong> Doune Castle (Monty Python!), Lake of Menteith</li>
            <li><strong>Ziel:</strong> Callander (Tor zu Trossachs)</li>
          </ul>

          <h4>Bannockburn Heritage Centre</h4>
          <ul>
            <li>5km südlich von Stirling</li>
            <li>Schlachtfeld von 1314 (Robert the Bruce vs. England)</li>
            <li>Interaktives Museum</li>
          </ul>

          <h3>Ochil Hills</h3>
          <div className="pro-tip">
            <h3><TreeDeciduous size={20} /> Hillfoot Villages Route</h3>
            <p>Charmante Route entlang der Ochil Hills mit historischen Dörfern:</p>
            <ul>
              <li><strong>Route:</strong> Stirling - Dollar - Alva - Tillicoultry (40km)</li>
              <li><strong>Anstiege:</strong> Moderat (500m HM)</li>
              <li><strong>Highlights:</strong> Dollar Glen, Castle Campbell</li>
            </ul>
          </div>
        </section>

        <section>
          <h2>5. Lanarkshire & Clyde Valley</h2>
          
          <h3>Der industrielle Süden</h3>
          <p>
            Lanarkshire war das Herz der schottischen Industriellen Revolution. Heute: 
            Regenerierte Flussrouten und UNESCO-Welterbe New Lanark.
          </p>

          <h4>Clyde Walkway (by bike: 60km, 500m HM)</h4>
          <ul>
            <li><strong>Route:</strong> Glasgow - Lanark (folgt River Clyde)</li>
            <li><strong>Oberfläche:</strong> Mix aus Asphalt & Gravel</li>
            <li><strong>Highlights:</strong> Falls of Clyde, New Lanark (UNESCO-Dorf)</li>
            <li><strong>Dauer:</strong> 1-2 Tage</li>
          </ul>

          <h4>New Lanark</h4>
          <div className="info-box">
            <ul>
              <li><strong>UNESCO-Welterbe:</strong> Erhaltenes Industriedorf aus 1786</li>
              <li><strong>Robert Owen:</strong> Pionier der sozialen Reform (Arbeiterbedingungen)</li>
              <li><strong>Besichtigung:</strong> Museum, Mühle, Worker's Houses</li>
              <li><strong>Falls of Clyde:</strong> Schöne Wasserfälle (kurze Wanderung)</li>
            </ul>
          </div>
        </section>

        <section>
          <h2>6. Praktische Infos Lowlands</h2>
          
          <h3>Infrastruktur & Versorgung</h3>
          <ul>
            <li><strong>Deutlich besser als Highlands!</strong> Shops, Cafés alle 20-30km</li>
            <li><strong>Unterkünfte:</strong> B&Bs überall, Hotels in größeren Städten</li>
            <li><strong>Bike Shops:</strong> In jeder größeren Stadt (Galashiels, Peebles, Ayr, Stirling)</li>
            <li><strong>Verkehr:</strong> Mehr als Highlands, aber immer noch moderat</li>
          </ul>

          <h3>Wetter & Beste Reisezeit</h3>
          <div className="data-grid">
            <div className="data-item">
              <strong>April - Mai:</strong> Frühling, grüne Hügel, weniger Touristen
            </div>
            <div className="data-item">
              <strong>Juni - August:</strong> Beste Zeit, Common Ridings Events!
            </div>
            <div className="data-item">
              <strong>September - Oktober:</strong> Herbstfarben, ruhiger, gutes Wetter
            </div>
            <div className="data-item">
              <strong>November - März:</strong> Möglich, aber kühl & nass
            </div>
          </div>

          <h3>Wetter-Vergleich zu Highlands</h3>
          <ul>
            <li><strong>Weniger Regen:</strong> ~50% weniger als Westküste</li>
            <li><strong>Wärmer:</strong> 2-3°C wärmer als Highlands im Durchschnitt</li>
            <li><strong>Weniger Wind:</strong> Hügel geben Schutz</li>
            <li><strong>Keine Midges-Hölle:</strong> Midges vorhanden, aber nicht so extrem</li>
          </ul>

          <h3>Schwierigkeit & Training</h3>
          <div className="info-box">
            <h4>Lowlands = Perfekt für...</h4>
            <ul>
              <li><strong>Anfänger:</strong> Keine extremen Anstiege</li>
              <li><strong>Familien:</strong> Kürzere Etappen möglich, gute Infrastruktur</li>
              <li><strong>Kultur-Fans:</strong> Viel Geschichte pro km!</li>
              <li><strong>Entspannte Touren:</strong> Zeit für Besichtigungen</li>
              <li><strong>Training für Highlands:</strong> Gute Vorbereitung!</li>
            </ul>
          </div>
        </section>

        <section>
          <h2>7. Literatur & Kultur</h2>
          
          <h3>Walter Scott Country</h3>
          <div className="pro-tip">
            <h3><BookOpen size={20} /> Auf Walter Scott's Spuren</h3>
            <p>
              Sir Walter Scott (1771-1832) = schottischer Nationaldichter, prägte das 
              romantische Bild Schottlands weltweit.
            </p>
            <ul>
              <li><strong>Abbotsford House:</strong> Scott's Heim (Melrose, 5km)</li>
              <li><strong>Scott's View:</strong> Sein Lieblingsausblick (B6356)</li>
              <li><strong>Dryburgh Abbey:</strong> Scott's Grab</li>
              <li><strong>Werke:</strong> Ivanhoe, Rob Roy, Lady of the Lake</li>
            </ul>
          </div>

          <h3>Robert Burns</h3>
          <ul>
            <li><strong>Burns Cottage (Alloway):</strong> Geburtshaus</li>
            <li><strong>Burns Night (25. Jan.):</strong> Nationale Feier mit Haggis!</li>
            <li><strong>Berühmte Werke:</strong> "Auld Lang Syne", "Tam o' Shanter", "A Red, Red Rose"</li>
          </ul>

          <h3>Border Ballads</h3>
          <p>
            Die Borders haben eine reiche Tradition mündlich überlieferter Balladen über 
            Raubzüge, Schlachten und Liebesgeschichten. Viele von Walter Scott aufgezeichnet.
          </p>
        </section>

        <div className="pro-tip">
          <h3>🎯 Top 5 Lowlands-Routen Ranking</h3>
          <ol>
            <li><strong>Borders Abbeys Way (100km):</strong> Geschichte, Kultur, moderate Anstiege - perfekte Mischung!</li>
            <li><strong>Tweed Cycle Route (150km):</strong> Landschaftlich schön, folgt Fluss, entspannt</li>
            <li><strong>Ayrshire Coastal (120km):</strong> Flach, Burns-Kultur, Arran-Aussicht</li>
            <li><strong>Burns Heritage Trail (80km):</strong> Literatur-Fans, einfach zu fahren</li>
            <li><strong>Stirling - Callander (70km):</strong> Geschichte + Natur, Tor zu Highlands</li>
          </ol>
        </div>

        <div className="info-box">
          <h4>📚 Weiterführende Guides</h4>
          <ul>
            <li><Link to="/wissen/ausruestung">🎒 Ausrüstung</Link> - Weniger extrem als Highlands nötig</li>
            <li><Link to="/wissen/fahrtechnik">🚴 Fahrtechnik</Link> - Hügel-Strategien</li>
            <li><Link to="/wissen/verkehrsregeln">🚦 Verkehrsregeln</Link> - Auch hier gilt Linksverkehr!</li>
          </ul>
        </div>

        <div className="download-section">
          <h3>📥 Downloads</h3>
          <button className="download-btn" onClick={() => window.print()}>
            Lowlands & Borders Guide als PDF speichern
          </button>
        </div>
      </article>
    </div>
  );
}
